import {useState, useCallback, useRef} from 'react'

import {useCreateSamplingPointMutation} from '../hooks/mutations/useCreateSamplingPointMutation'
import {useDeleteSamplingPointMutation} from '../hooks/mutations/useDeleteSamplingPointMutation'
import {useUpdateSamplingPointMutation} from '../hooks/mutations/useUpdateSamplingPointMutation'
import {
  EquipmentOption,
  LimsSamplingPoint,
  RowData,
  SamplingPointEquipmentType,
  EquipmentOptionType
} from '../types'

import {useUrlParam} from '@settings/modules/routing'

const isRowDataEqual = (data1: RowData, data2: RowData): boolean => {
  return (
    data1.typeOption?.id === data2.typeOption?.id &&
    data1.equipmentOption?.id === data2.equipmentOption?.id &&
    data1.upmDepartmentOption?.id === data2.upmDepartmentOption?.id
  )
}

const getEquipmentIdParamBySamplingPointType = (
  samplingPointType: SamplingPointEquipmentType,
  equipmentOption: EquipmentOption | null
) => {
  if (!equipmentOption) {
    return {}
  }

  switch (samplingPointType) {
    case SamplingPointEquipmentType.asset:
      return equipmentOption.type === EquipmentOptionType.grindingCircuit
        ? {grindingCircuitId: equipmentOption.id}
        : {assetId: equipmentOption.id}

    case SamplingPointEquipmentType.storage:
      return {storageId: equipmentOption.id}

    case SamplingPointEquipmentType.other:
    default:
      return {}
  }
}

export const useSamplingPointsManagement = (limsSamplingPoints: LimsSamplingPoint[]) => {
  const plantCode = useUrlParam('plantCode')

  // Single source of truth for original/baseline values
  const originalValuesRef = useRef<Map<number, RowData>>(new Map())

  // Only stores current edited values (not original)
  const [editedRows, setEditedRows] = useState<Map<number, RowData>>(new Map())
  const [isSavingSamplingPoints, setIsSavingSamplingPoints] = useState(false)

  const {mutateAsync: createSamplingPoint} = useCreateSamplingPointMutation()
  const {mutateAsync: updateSamplingPoint} = useUpdateSamplingPointMutation()
  const {mutateAsync: deleteSamplingPoint} = useDeleteSamplingPointMutation()

  const getRowData = useCallback(
    (limsSamplingPointId: number): RowData | undefined => {
      return editedRows.get(limsSamplingPointId)
    },
    [editedRows]
  )

  const getOriginalRowData = useCallback((limsSamplingPointId: number): RowData | undefined => {
    return originalValuesRef.current.get(limsSamplingPointId)
  }, [])

  const updateRowData = useCallback(
    (limsSamplingPointId: number, newRowData: RowData, initialRowData?: RowData) => {
      if (initialRowData && !originalValuesRef.current.has(limsSamplingPointId)) {
        originalValuesRef.current.set(limsSamplingPointId, initialRowData)
      }

      const originalRowData = originalValuesRef.current.get(limsSamplingPointId) ?? initialRowData

      setEditedRows((prev) => {
        const newEditedRowsMap = new Map(prev)

        if (!originalRowData) {
          newEditedRowsMap.set(limsSamplingPointId, newRowData)
          return newEditedRowsMap
        }

        if (isRowDataEqual(newRowData, originalRowData)) {
          newEditedRowsMap.delete(limsSamplingPointId)
        } else {
          newEditedRowsMap.set(limsSamplingPointId, newRowData)
        }

        return newEditedRowsMap
      })
    },
    []
  )

  const clearAllRows = useCallback(() => {
    // Update baseline with currently edited values (they are now saved)
    editedRows.forEach((currentData, limsSamplingPointId) => {
      originalValuesRef.current.set(limsSamplingPointId, currentData)
    })

    // Clear edited rows since everything is now saved
    setEditedRows(new Map())
  }, [editedRows])

  const isRowEdited = useCallback(
    (limsSamplingPointId: number): boolean => {
      return editedRows.has(limsSamplingPointId)
    },
    [editedRows]
  )

  const saveRow = useCallback(
    async (limsSamplingPoint: LimsSamplingPoint): Promise<void> => {
      const currentData = editedRows.get(limsSamplingPoint.id)
      if (!currentData) {
        return Promise.resolve()
      }

      const newTypeOption = currentData.typeOption
      const newEquipmentOption = currentData.equipmentOption
      const newUpmDepartmentOption = currentData.upmDepartmentOption
      const existingSamplingPoint = limsSamplingPoint.samplingPoint

      // User tries to delete existing mapping
      if (newTypeOption === null) {
        if (existingSamplingPoint) {
          await deleteSamplingPoint({
            plantCode,
            samplingPointId: existingSamplingPoint.id
          })
        }
      }
      // User tries to create a new mapping
      else if (!existingSamplingPoint) {
        await createSamplingPoint({
          plantCode,
          type: newTypeOption.label,
          limsSamplingPointId: limsSamplingPoint.id,
          upmDepartmentId: newUpmDepartmentOption?.id,
          ...getEquipmentIdParamBySamplingPointType(newTypeOption.type, newEquipmentOption)
        })
      }
      // User tries to update existing mapping
      else {
        await updateSamplingPoint({
          plantCode,
          type: newTypeOption.label,
          samplingPointId: existingSamplingPoint.id,
          upmDepartmentId: newUpmDepartmentOption?.id,
          ...getEquipmentIdParamBySamplingPointType(newTypeOption.type, newEquipmentOption)
        })
      }
    },
    [editedRows, plantCode, createSamplingPoint, updateSamplingPoint, deleteSamplingPoint]
  )

  const handleSamplingPointsMapping = useCallback(async () => {
    // Filter only rows that have changes in the store
    const rowsToSubmit = limsSamplingPoints.filter((limsSamplingPoint) =>
      editedRows.has(limsSamplingPoint.id)
    )

    if (rowsToSubmit.length === 0) {
      return
    }

    setIsSavingSamplingPoints(true)

    try {
      // Save all rows in parallel
      await Promise.all(rowsToSubmit.map((limsSamplingPoint) => saveRow(limsSamplingPoint)))
      clearAllRows()
    } catch (error) {
      // Don't clear on error - let user retry
    } finally {
      setIsSavingSamplingPoints(false)
    }
  }, [limsSamplingPoints, editedRows, saveRow, clearAllRows])

  return {
    getRowData,
    getOriginalRowData,
    updateRowData,
    isRowEdited,
    handleSamplingPointsMapping,
    isSavingSamplingPoints,
    hasChanges: editedRows.size > 0
  }
}
